import React from "react";
import { AiOutlineClose } from "react-icons/ai";
import { BsSliders } from "react-icons/bs";
import { Modal } from "react-bootstrap";

import BoletoItem from "components/Boletos/Item";
import BillCompensationBanner from "components/BillCompensationBanner";
import ContainedButton from "components/layout/form/containedButton";
import Loading from "components/layout/loading";
import ModalContent from "components/Boletos/ModalContent";
import ModalFilter from "components/Boletos/ModalFilter";
import ModalNothingContained from "components/Boletos/ModalNothingContained";
import Navbar from "components/MI/Navbar";
import OutlinedButton from "components/layout/form/outlinedButton";
import Pagination from "components/layout/Pagination";
import TextSelect from "components/layout/form/TextSelect";
import UserMenu from "components/layout/userMenuNew";
import TourBalloon from "components/NewTour/TourBallon";
import ModalAutomaticDebit from "features/modal-debito-automatico";
import { useBoletosPage } from "features/boletos/hooks/useBoletosPage";
import type { BillListItem } from "features/boletos/types";
import theme from "styles/theme";
import {
  BillsContentWrap,
  Body,
  BoxFilters,
  BoxPaddingContainer,
  Container,
  Content,
  ContentButtons,
  Divider,
  FilterWrap,
  FiltersAndActionsWrapper,
  Header,
  HeaderInfos,
  HeaderTitle,
  NotContent,
  StyledModal,
} from "../styles";

function billRowKey(bill: BillListItem, page: number, index: number): string {
  const id = bill.invoice["id"];
  if (typeof id === "string" || typeof id === "number") {
    return String(id);
  }
  return `bill-${page}-${index}`;
}

const BoletosPage = (): JSX.Element => {
  const {
    bills,
    isLoading,
    isFilter,
    isShowModalContent,
    setIsShowModalContent,
    isShowModalFilter,
    setIsShowModalFilter,
    isShowModalNothingContained,
    setIsShowModalNothingContained,
    isShowModalAutomaticDebit,
    setIsShowModalAutomaticDebit,
    modalPayment,
    modalInstallments,
    status,
    selectedPeriod,
    totalPages,
    selectedPage,
    setSelectedPage,
    handleSelectedPeriod,
    handleStatus,
    handleShow,
    showTour,
    tourStep,
    tourType,
    clearFiltersHref,
  } = useBoletosPage();

  return (
    <Navbar selected="consulta-debitos" openSubMenu>
      <UserMenu intro />
      <Container>
        <Header>
          <HeaderInfos>
            <HeaderTitle>Histórico de boletos</HeaderTitle>
            <BillCompensationBanner />
          </HeaderInfos>
        </Header>
        <Body>
          <FiltersAndActionsWrapper>
            <FilterWrap
              showTour={
                tourStep === 2 && tourType === "consulta-debitos" && showTour
              }
            >
              <BoxPaddingContainer>
                <BoxFilters>
                  <TextSelect
                    label="Ordenar por"
                    name="selectedPeriod"
                    type="text"
                    value={selectedPeriod}
                    onChange={handleSelectedPeriod}
                  >
                    <option key="recentToOld" value="recentToOld">
                      Mais recente para o mais antigo
                    </option>
                    <option key="oldToRecent" value="oldToRecent">
                      Mais antigo para o mais recente
                    </option>
                    <option key="old15days" value="old15days">
                      Últimos 15 dias
                    </option>
                    <option key="old30days" value="old30days">
                      Últimos 30 dias
                    </option>
                    <option key="old60days" value="old60days">
                      Últimos 60 dias
                    </option>
                    <option key="old90days" value="old90days">
                      Últimos 90 dias
                    </option>
                  </TextSelect>
                  <TextSelect
                    label="Situação"
                    name="status"
                    type="text"
                    value={status}
                    onChange={handleStatus}
                  >
                    <option key="todos" value="TODOS">
                      Todos
                    </option>
                    <option key="vencido" value="VENCIDO">
                      Vencido
                    </option>
                    <option key="pago" value="PAGO">
                      Pago
                    </option>
                  </TextSelect>
                </BoxFilters>
              </BoxPaddingContainer>
              {showTour &&
                tourStep === 2 &&
                tourType === "consulta-debitos" && (
                  <TourBalloon
                    title="Filtros"
                    content="Aqui você pode filtrar sua consulta."
                    position={{
                      left: "50%",
                      top: "48px",
                      transform: "translateX(-50%)",
                      arrowPosition: "top",
                      arrowTop: "-27px",
                      maxWidth: 320,
                      boxShadow: true,
                    }}
                  />
                )}
            </FilterWrap>
            <ContentButtons>
              <BillsContentWrap
                showTour={
                  tourStep === 1 && tourType === "consulta-debitos" && showTour
                }
              >
                <OutlinedButton
                  onClick={() => setIsShowModalNothingContained(true)}
                >
                  Solicitar carta de quitação
                </OutlinedButton>
                {showTour &&
                  tourStep === 1 &&
                  tourType === "consulta-debitos" && (
                    <TourBalloon
                      title="Carta de quitação"
                      content="Solicite aqui sua carta de quitação de débitos."
                      position={{
                        top: "48px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        arrowPosition: "top",
                        maxWidth: 320,
                        arrowTop: "-30%",
                        boxShadow: true,
                      }}
                    />
                  )}
              </BillsContentWrap>
              <ContainedButton
                LeadingIcon={BsSliders}
                propsLeadingIcon={{
                  color: theme.colors.white,
                  size: 24,
                }}
                onClick={() => setIsShowModalFilter(true)}
                disabled={isLoading}
              >
                Filtros
              </ContainedButton>
              {isFilter && (
                <OutlinedButton
                  LeadingIcon={AiOutlineClose}
                  propsLeadingIcon={{
                    color: theme.colors.blue,
                    size: 24,
                  }}
                  onClick={clearFiltersHref}
                  disabled={isLoading}
                >
                  Limpar
                </OutlinedButton>
              )}
            </ContentButtons>
          </FiltersAndActionsWrapper>
          <BillsContentWrap
            showTour={
              tourStep === 3 && tourType === "consulta-debitos" && showTour
            }
          >
            {bills.length > 0 ? (
              <Content>
                {bills.map((bill, index) => (
                  <React.Fragment key={billRowKey(bill, selectedPage, index)}>
                    {index !== 0 && <Divider />}
                    <BoletoItem
                      installment={bill.installment}
                      invoice={bill.invoice}
                      open={() => handleShow(bill.invoice, bill.installment)}
                    />
                  </React.Fragment>
                ))}
              </Content>
            ) : (
              <NotContent>
                {isFilter ? (
                  <span>Você não possui boletos para esse filtro!</span>
                ) : (
                  <>
                    <span>Você não possui boletos!</span>
                    <span>
                      {
                        "Caso tenha se cadastrado recentemente, a primeira cobrança individualizada, em seu nome, será processada em até "
                      }
                      <span>60 dias</span>.
                    </span>
                  </>
                )}
              </NotContent>
            )}
            {showTour && tourStep === 3 && tourType === "consulta-debitos" && (
              <TourBalloon
                title="Boletos"
                content="Aqui você encontra todos os seus boletos e você poderá baixar, imprimir ou enviar por e-mail."
                position={{
                  left: "56%",
                  top: "0",
                  arrowPosition: "unset",
                  maxWidth: 320,
                  arrowLeft: "-10%",
                  boxShadow: true,
                }}
              />
            )}
          </BillsContentWrap>
          {totalPages > 1 && (
            <Pagination
              totalPages={totalPages}
              selectedPage={selectedPage}
              onPageChange={setSelectedPage}
              isLoading={isLoading}
            />
          )}
        </Body>
      </Container>
      <Modal
        show={isShowModalContent}
        onHide={() => setIsShowModalContent(false)}
        size="lg"
        dialogClassName="modal-90w"
        aria-labelledby="example-custom-modal-styling-title"
        animation={false}
        centered
      >
        <ModalContent
          // @ts-expect-error ModalContent legado sem tipagem alinhada à API de boletos
          close={() => setIsShowModalContent(false)}
          payment={modalPayment}
          installment={modalInstallments}
        />
      </Modal>
      <StyledModal
        show={isShowModalFilter}
        onHide={() => setIsShowModalFilter(false)}
        aria-labelledby="example-custom-modal-styling-title"
        animation={false}
        centered
      >
        <ModalFilter close={() => setIsShowModalFilter(false)} />
      </StyledModal>
      <ModalNothingContained
        show={isShowModalNothingContained}
        onClose={() => setIsShowModalNothingContained(false)}
      />
      <ModalAutomaticDebit
        show={isShowModalAutomaticDebit}
        onClose={() => setIsShowModalAutomaticDebit(false)}
      />
      {isLoading && <Loading />}
    </Navbar>
  );
};

export default BoletosPage;
